/**
 * JourneyPanel.tsx — "Patient journey": the whole of one patient's route
 * through the product on a single rail, with the outstanding step and the
 * controls that perform it pinned to the top.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 * CTO design, 2026-09-07. Lives in the deal drawer's OVERVIEW tab; CLINICIAN and
 * ADMIN both see it.
 *
 * ── WHY IT EXISTS ────────────────────────────────────────────────────────────
 * Every fact on this panel was already somewhere in the portal, and that was the
 * problem: the questionnaire's progress was behind one tab, the clinical
 * decision behind another, the money behind a third that clinicians cannot open
 * at all, and the pharmacy tail behind a fourth. Answering "what is this patient
 * waiting for?" meant opening four tabs and knowing which order they happened
 * in. The rail answers it in one glance and the ACTION BAND answers it in one
 * sentence.
 *
 * ── ⚠️ THE BAND IS THE POINT, NOT THE RAIL ───────────────────────────────────
 * The rail is history. The band is the job. It states the outstanding step,
 * whose move it is, and carries the buttons that perform it — which is why it
 * sits directly under the header, above everything else, and is a
 * `role="status"` region so a screen-reader user gets it without hunting.
 *
 * ── ⚠️ COLOUR IS NEVER THE ONLY SIGNAL ───────────────────────────────────────
 * The four step states are encoded in SHAPE (filled tick / ringed dot / barred
 * ring / dashed outline) as well as colour, because "colour alone fails a
 * colour-blind reader and a screenshot" (CTO) — and each marker also carries a
 * `gps-sr-only` word, so the state survives into the accessibility tree rather
 * than living only in the pixels. The same rule the estate's Badge and Alert
 * already follow.
 *
 * ── ⚠️ MONEY ─────────────────────────────────────────────────────────────────
 * Nothing on this file formats a figure. Every pence value is resolved in
 * `journeyModel.ts` behind a `role === 'ADMIN'` gate on a posture whose pence
 * keys only exist on the ADMIN projection — two independent guards — and reaches
 * this component as a finished string. `clinicians/clinicians.spec.tsx` sweeps
 * the whole clinician drawer for `£ / pence / price`.
 *
 * ── REUSED, NOT REWRITTEN ────────────────────────────────────────────────────
 * `PlanLinkActions` and `PaymentLinkAction` are mounted here as they are. The
 * plan link MOVED into the band from the foot of this tab (it had one home
 * before and it has one home now — the band is simply a better one, because the
 * control now sits beside the sentence that explains why to press it).
 */
import type { CSSProperties, ReactNode } from 'react';
import type { DealCard, DealDetail, StaffOrder, UserRole } from '../api/types';
import { formatUtcDate } from '../lib/format';
import { PaymentLinkAction } from '../admin/PaymentLinkAction';
import { PharmacyLinkAction } from '../admin/PharmacyLinkAction';
import { orderHasPharmacyWork } from './paymentPosture';
import { SectionTitleInline } from '../staff/ui';
import { ProgressRail } from '../ui/kit';
import { buildJourney, type JourneyBand, type JourneyStep, type JourneyStepState } from './journeyModel';
import { PlanLinkActions } from './PlanLinkActions';
import { useQuestionnaireProgress } from './questionnaireProgress';

/* ── THE FOUR MARKERS ───────────────────────────────────────────────────────
 *
 * ⚠️ EACH ONE IS A DIFFERENT SHAPE, and the shapes are the contract. Two states
 * that differ only in hue are one state to a colour-blind reader, to a
 * greyscale print and to a screenshot in a support ticket — and this panel's
 * whole job is to be read at a glance.
 *
 * WCAG 2.1 AA, measured against `--gps-surface` (#ffffff), the drawer's ground:
 *  done       #2e7d32 fill, white tick        5.13:1 — text-grade, well past 3:1
 *  now        #8a5a00 ring + dot              5.93:1 — NOT the 5.02:1 in the
 *             token's own comment: that figure is this ink on the warn TINT
 *             (--gps-warn-bg), which is where the estate's chip pair uses it.
 *             On this panel the ring sits on the drawer's white surface, and
 *             the ratio is higher. Same ink, different ground, different
 *             number — measure per ground, never carry a figure across.
 *  stopped    #e76f51 ring + bar              3.09:1 — clears 1.4.11's 3:1 for a
 *             meaningful graphic, and it is the ONLY token in this palette the
 *             CTO's "coral ring" names. It is deliberately the tightest of the
 *             four, which is precisely why the BAR and the `gps-sr-only` word
 *             carry the meaning and the colour only reinforces it. ⚠️ Re-measure
 *             before reusing this hue on a different ground.
 *  not_reached #708278 dashed ring            4.08:1
 */
const MARKER_WORD: Record<JourneyStepState, string> = {
  done: 'Done',
  now: 'Happening now',
  stopped: 'Stopped here',
  not_reached: 'Not reached',
};

const MARKER_SIZE = 18;

function StepMarker({ state }: { state: JourneyStepState }) {
  const base: CSSProperties = {
    width: `${MARKER_SIZE}px`,
    height: `${MARKER_SIZE}px`,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    boxSizing: 'border-box',
    flex: 'none',
  };
  let face: ReactNode = null;
  let style: CSSProperties = base;

  if (state === 'done') {
    style = { ...base, background: 'var(--gps-primary)' };
    // A filled tick. Stroke, not a glyph font: it has to survive a print.
    face = (
      <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
        <path d="M2.5 6.4 4.8 8.7 9.5 3.6" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  } else if (state === 'now') {
    style = { ...base, border: '2px solid var(--gps-warn-text)', background: 'var(--gps-surface)' };
    // A ringed dot — a ring with a solid centre, distinct from a plain outline.
    face = <span aria-hidden="true" style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--gps-warn-text)' }} />;
  } else if (state === 'stopped') {
    style = { ...base, border: '2px solid var(--gps-coral)', background: 'var(--gps-surface)' };
    // A ring with a BAR through it — the universal "no further" mark, and the
    // one shape here that reads as negative without any colour at all.
    face = <span aria-hidden="true" style={{ width: '9px', height: '2px', borderRadius: '1px', background: 'var(--gps-coral)' }} />;
  } else {
    style = { ...base, border: '1.5px dashed var(--gps-border-strong)', background: 'transparent' };
  }

  return (
    <span style={style}>
      {face}
      <span className="gps-sr-only">{MARKER_WORD[state]}</span>
    </span>
  );
}

/* ── THE ACTION BAND ────────────────────────────────────────────────────────── */

const BAND_TOKENS: Record<JourneyBand['tone'], { bg: string; text: string; edge: string }> = {
  // AMBER — blocked on somebody. The estate's warn pair (5.02:1).
  amber: { bg: 'var(--gps-warn-bg)', text: 'var(--gps-warn-text)', edge: 'var(--gps-warn-text)' },
  // GREEN — the next step runs on its own. The estate's info/success pair.
  green: { bg: 'var(--gps-primary-light)', text: 'var(--gps-green-text)', edge: 'var(--gps-primary)' },
  // NEUTRAL — nothing is outstanding at all. See `JourneyBand.tone` for why a
  // closed deal must not wear either of the other two.
  neutral: { bg: 'var(--gps-neutral-tint)', text: 'var(--gps-text)', edge: 'var(--gps-border-strong)' },
};

/**
 * WHOSE MOVE IT IS. A small pill, in the band's own ink — the OWNER is the fact
 * a desk scans for first ("is this mine?"), so it leads the band rather than
 * being buried in the sentence.
 */
function OwnerPill({ owner, text }: { owner: string; text: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        border: `1px solid ${text}`,
        color: text,
        borderRadius: 'var(--gps-r-pill)',
        fontSize: '10.5px',
        fontWeight: 700,
        letterSpacing: '0.06em',
        padding: '2px 8px',
        flex: 'none',
      }}
    >
      {owner}
    </span>
  );
}

/* ── THE PANEL ──────────────────────────────────────────────────────────────── */

export function JourneyPanel({
  deal,
  detail,
  order,
  role,
  style,
}: {
  deal: DealCard;
  detail: DealDetail;
  /**
   * The `StaffOrder` behind `orderCaseRef`, when the drawer has resolved one.
   * The drawer already fetches it once per order-backed drawer; the panel never
   * issues that read itself.
   */
  order: StaffOrder | null;
  role: UserRole;
  style?: CSSProperties;
}) {
  /**
   * The ONLY read this panel owns. Two parallel GETs, both already used by this
   * drawer, and neither blocks anything: the Questionnaire rung renders without
   * a progress line until they land and keeps rendering without one if they
   * fail. See `questionnaireProgress.ts` for why the count cannot come off the
   * deal aggregate, and for the R19 guarantee that the answer VALUES never enter
   * React state.
   */
  const { progress } = useQuestionnaireProgress(deal.assessmentId);

  const journey = buildJourney({
    deal,
    assessment: detail.assessment,
    callback: detail.callback,
    activity: detail.activity,
    outcome: detail.outcome,
    order,
    role,
    questionnaire: progress,
  });
  const band = journey.band;
  const bandTokens = BAND_TOKENS[band.tone];

  // A PAID order the pharmacy still has WORK on — the only kind a pharmacy
  // sign-in link is for. `captured`, not `authorised`: a hold is not a paid
  // order. Never a TEST order: the pharmacy has been told not to dispense it.
  // And not once it is DISPATCHED (CTO, 2026-09-11): that month is done, and a
  // repeat month reopens it on its own. Reads the POSTURE's order state — the
  // same live case the link is minted for; the server refuses on the same set.
  const posture = deal.paymentPosture;
  const paidOrderCaseRef =
    posture?.state === 'captured' && posture.isTest !== true && orderHasPharmacyWork(posture.orderState)
      ? posture.orderCaseRef
      : null;

  return (
    <section
      aria-labelledby="deal-journey-title"
      style={{
        marginTop: 'var(--gps-s-4)',
        paddingTop: 'var(--gps-s-3)',
        borderTop: '1px solid var(--gps-border)',
        ...style,
      }}
    >
      {/* HEADER — the title and the ROUTE chip. The chip names what happens
          next, not where the record came from: "Widget form" is provenance and
          the board card already wears it; "Web form → self-serve" tells the
          reader whether a human is in this patient's loop. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gps-s-3)', flexWrap: 'wrap', marginBottom: 'var(--gps-s-3)' }}>
        <SectionTitleInline>
          <span id="deal-journey-title">Patient journey</span>
        </SectionTitleInline>
        <span
          style={{
            background: 'var(--gps-surface-2)',
            color: 'var(--gps-muted)',
            border: '1px solid var(--gps-border)',
            borderRadius: 'var(--gps-r-pill)',
            fontSize: '11.5px',
            fontWeight: 600,
            padding: '3px 10px',
          }}
        >
          {journey.entryLabel}
        </span>
      </div>

      {/* THE ACTION BAND. `role="status"` — a polite live region: this is the
          one thing on the panel a reader must not have to go looking for, and
          it changes under them when the drawer reloads after a move. NOT
          `role="alert"`: the drawer already has exactly one assertive region
          (the action-needed banner above the tabs) and a second one competing
          with it would make both easier to ignore. */}
      <div
        role="status"
        style={{
          background: bandTokens.bg,
          color: bandTokens.text,
          borderLeft: `3px solid ${bandTokens.edge}`,
          borderRadius: 'var(--gps-r-md)',
          padding: '12px 14px',
          display: 'grid',
          gap: 'var(--gps-s-2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gps-s-2)', flexWrap: 'wrap' }}>
          {band.owner ? <OwnerPill owner={band.owner} text={bandTokens.text} /> : null}
          <span style={{ fontWeight: 700, fontSize: '14px', lineHeight: 1.35 }}>{band.headline}</span>
        </div>
        {band.detail ? <div style={{ fontSize: '13px', lineHeight: 1.45 }}>{band.detail}</div> : null}

        {/* THE CONTROLS THAT PERFORM THE OUTSTANDING STEP, in the band that
            names it. Both are the EXISTING components, mounted unchanged.
            ⚠️ The Stripe payment link is gated far more narrowly than "ADMIN" —
            see `paymentLinkAvailable` in journeyModel.ts before widening it. */}
        {band.actions.planLink && deal.assessmentId ? (
          <PlanLinkActions
            assessmentId={deal.assessmentId}
            lastSentAt={detail.assessment?.planLinkLastSentAt}
            viewedAt={detail.deal.planViewedAt}
            style={{ marginTop: '2px' }}
          />
        ) : null}
        {band.actions.paymentLink && deal.paymentPosture?.orderCaseRef ? (
          <PaymentLinkAction orderCaseId={deal.paymentPosture.orderCaseRef} />
        ) : null}
        {/* HOTFIX 2026-09-11 — a fresh PHARMACY sign-in link, for an ADMIN, on a
            PAID order. The pharmacy's own link is single-use and expires in an
            hour, so a paid order could sit undispensed with nobody able to let
            them in. The server is the control (ADMIN-only, refuses a test order
            and one not yet sent to the pharmacy); this gate only keeps the
            button off screens where it could never succeed. */}
        {role === 'ADMIN' && paidOrderCaseRef ? <PharmacyLinkAction orderCaseId={paidOrderCaseRef} /> : null}
      </div>

      {/* THE RAIL. One list per phase, so assistive tech announces "list of 5"
          per group rather than one undifferentiated run of fourteen — and the
          phase heading is a real heading, not a styled div. */}
      <div style={{ marginTop: 'var(--gps-s-4)', display: 'grid', gap: 'var(--gps-s-4)' }}>
        {journey.phases.map((phase) => (
          <div key={phase.id}>
            {/* The "small header rule": the phase name, then a hairline filling
                the row. Decorative, so the rule itself is aria-hidden. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gps-s-2)', marginBottom: 'var(--gps-s-2)' }}>
              {/* h3: the panel's own title is the h2 (SectionTitleInline), so the
                  phase names are the level below it. Not an h4 — skipping a
                  level is an axe `heading-order` violation and, more to the
                  point, breaks heading navigation for a screen-reader user
                  moving through the drawer. */}
              <h3
                style={{
                  margin: 0,
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                  color: 'var(--gps-muted)',
                }}
              >
                {phase.title}
              </h3>
              {/* ⚠️ THE OWNER IS A CAPTION, NOT A PILL, and the distinction is
                  load-bearing. It says whose step this stage is IN THE MODEL —
                  true of every card, including one nobody is waiting on. The
                  band above has the only pill, and that one IS a claim about
                  who owes the next move on THIS card. Two pills in two
                  grammars on one panel get read as one, and then a group
                  labelled "Clinician" on a finished case looks like a job.

                  Inside the h3's flex row but AFTER the heading text, and
                  outside the <h3> itself, so it does not join the accessible
                  name — a screen-reader user navigating by heading hears
                  "Clinical decision", not "Clinical decision Clinician". */}
              {phase.owner ? (
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 500,
                    letterSpacing: '0.04em',
                    color: 'var(--gps-muted)',
                    textTransform: 'none',
                    opacity: 0.85,
                  }}
                >
                  {phase.owner}
                </span>
              ) : null}
              <span aria-hidden="true" style={{ flex: 1, height: '1px', background: 'var(--gps-border)' }} />
            </div>
            {/* The group sentence: what is true of the GROUP rather than of any
                rung in it — the S-2 capture gap, and which of the card and the
                decision came first. It is the ONLY place this panel states that
                order, because a sentence can say "not recorded" and a layout
                cannot (see `sequenceNote`). */}
            {phase.note ? (
              <p
                style={{
                  margin: '0 0 var(--gps-s-2) 0',
                  fontSize: '12px',
                  lineHeight: 1.45,
                  color: 'var(--gps-muted)',
                }}
              >
                {phase.note}
              </p>
            ) : null}
            <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid' }}>
              {phase.steps.map((step, index) => (
                <StepRow key={step.id} step={step} last={index === phase.steps.length - 1} />
              ))}
            </ol>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * ONE RUNG.
 *
 * ⚠️ THE RIGHT-HAND COLUMN IS ONLY EVER A DATE OR AN EM-DASH (CTO). Never a
 * count, never a status word, never a duration — the whole reason it is a fixed
 * column is so the dates line up down the panel and a reader can see the shape
 * of the journey in time. Everything else about a rung goes UNDER its name.
 */
function StepRow({ step, last }: { step: JourneyStep; last: boolean }) {
  const muted = step.state === 'not_reached';
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: `${MARKER_SIZE}px minmax(0, 1fr) auto`,
        gap: 'var(--gps-s-3)',
        alignItems: 'start',
        // Room for the connector to run between two markers.
        paddingBottom: last ? 0 : '14px',
        position: 'relative',
      }}
    >
      <div style={{ display: 'grid', justifyItems: 'center' }}>
        <StepMarker state={step.state} />
        {/* The connector — what makes this a RAIL rather than a list of ticks.
            Decorative (the list structure is what conveys sequence to assistive
            tech), and deliberately 2px rather than the 1px hairline used for
            the phase rules: at 1px in `--gps-border` it was invisible against
            the white drawer at real rendering sizes, which is the whole reason
            it exists. Still the palest border token — a rail that competes with
            the markers is worse than no rail. */}
        {last ? null : (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: `${MARKER_SIZE + 2}px`,
              bottom: '-2px',
              left: `${MARKER_SIZE / 2 - 1}px`,
              width: '2px',
              borderRadius: '1px',
              background: 'var(--gps-border)',
            }}
          />
        )}
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gps-s-2)', flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: '13.5px',
              fontWeight: step.state === 'now' || step.state === 'stopped' ? 700 : 500,
              color: muted ? 'var(--gps-muted)' : 'var(--gps-text)',
            }}
          >
            {step.label}
          </span>
          {/* WHO DID THIS STEP, when it is not a person on our side. "auto":
              capture, prescription issue and the pharmacy handoff all fire from
              one approval inside `fulfilment.service.ts`, and nobody presses
              anything. "pharmacy": a pharmacist records it in the dispensing
              console — dispatch (CTO, 2026-09-11: it is not automatic). */}
          {step.by ? (
            <span
              style={{
                background: 'var(--gps-neutral-tint)',
                color: 'var(--gps-muted)',
                borderRadius: 'var(--gps-r-sm)',
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                padding: '1px 5px',
              }}
            >
              {step.by}
            </span>
          ) : null}
        </div>
        {/* THE LINE UNDER THE NAME: the questionnaire's progress, the refund
            warning, the plan, the courier. Counts live here and only here. */}
        {step.note ? (
          <div style={{ fontSize: '12px', color: 'var(--gps-muted)', marginTop: '2px', lineHeight: 1.4 }}>{step.note}</div>
        ) : null}
        {step.percent !== undefined ? (
          <div style={{ display: 'flex', maxWidth: '180px', marginTop: '5px' }}>
            <ProgressRail percent={step.percent} label={`${step.label} progress`} />
          </div>
        ) : null}
      </div>

      {/* A real <time> when there is an instant, so the machine-readable date
          rides with the human one; an em-dash otherwise. `formatUtcDate`
          returns '' for an unparseable string, which falls back to the dash
          rather than rendering an empty cell. */}
      <div style={{ fontSize: '12.5px', color: 'var(--gps-muted)', whiteSpace: 'nowrap', paddingTop: '1px' }}>
        {step.at && formatUtcDate(step.at) ? (
          <time dateTime={step.at}>{formatUtcDate(step.at)}</time>
        ) : (
          <span aria-hidden="true">&mdash;</span>
        )}
      </div>
    </li>
  );
}
